import { createPasswordForFiles } from "../../../../utils/utils";

import { InvestigationDetails } from "js/components/Tenants/TenantReportDetails/typing";

/**
 * Pure builders for the "Descargas" block of the tenant report detail page.
 *
 * Extracted out of TenantReportDetails.tsx so the row logic can be unit-tested without
 * mounting the page (and therefore without antd / framer-motion / @multiburo/ui-components).
 * The report payload is intentionally `any`: `getTenantReportDetails` returns the raw axios
 * `data` and the detail query is not parameterized, so nothing type-checks these reads today.
 */

export interface DownloadItemData {
  name: string;
  type: string;
  status: string | undefined;
  documentPassword: string | undefined;
}

/** A requirement group (`required_documents[subfolder]`) has `slug` validated. */
export const isRequirementValid = (
  reportData: any,
  subfolder: string,
  slug: string,
): boolean => {
  const requirements = reportData?.required_documents?.[subfolder];
  const form = requirements?.find((doc: any) => doc?.slug === slug) || false;
  return form?.status === "valid";
};

/**
 * Password that opens the credit-report PDFs of an investigation.
 *
 * `person.type` comes from `Person.get_type_display()` and is normally "Natural"/"Legal",
 * but it is not guaranteed to be there. `createPasswordForFiles` silently returns the
 * persona-moral form (`#RFC#`, no birth date) for anything that isn't exactly "natural",
 * so an unknown type would hand the user a password that opens nothing, with no error.
 * Fall back to the investigation's own `legal_type` (same information, same object) and
 * emit NO password when neither is usable — showing nothing beats showing a wrong one.
 */
export const getPersonPassword = (
  investigation: InvestigationDetails | undefined | null,
): string | undefined => {
  const person = investigation?.person;
  if (!person?.rfc) return undefined;

  // Se valida contra el conjunto CONOCIDO, no solo contra "no vacio": el
  // comentario de arriba prometia proteger de un tipo irreconocible y el codigo
  // solo cubria el caso ausente. `createPasswordForFiles` trata TODO lo que no
  // sea exactamente "natural" como persona moral, asi que un valor localizado
  // ("Fisica") producia en silencio una contrasena que no abre el archivo.
  const KNOWN_TYPES = ["natural", "legal"] as const;
  const normalize = (value: unknown): string | undefined => {
    if (typeof value !== "string") return undefined;
    const lowered = value.trim().toLowerCase();
    return (KNOWN_TYPES as readonly string[]).includes(lowered)
      ? lowered
      : undefined;
  };
  const rawType =
    normalize(person.type) ?? normalize(investigation?.legal_type);
  if (!rawType) return undefined;

  return createPasswordForFiles(
    rawType as any,
    person.rfc,
    person?.birth_date ?? null,
  );
};

/**
 * Rows of the "Descargas" block. Returns `null` while there is no report yet, so the
 * caller stores `null` instead of fabricating rows with `status: undefined`.
 */
export const buildDownloadItems = (
  reportData: any,
): DownloadItemData[] | null => {
  if (!reportData) return null;

  const data: DownloadItemData[] = [];

  const tenantDocumentPassword = getPersonPassword(reportData.investigation);

  data.push({
    name: "Multireporte Inquilino",
    type: "tenant_report",
    status: reportData.status,
    documentPassword: tenantDocumentPassword,
  });

  // Tenant Express report.
  // The subfolder that holds the tenant's bdc-authorization is named by the BACKEND from
  // the report's own legal type (tenant-pf / tenant-pm), never from `person.legal_type`:
  // `person` is a nullable FK and is simply absent on some reports. Deriving the folder
  // from it used to crash the page, and guessing it with `person?.legal_type` would be
  // worse — it silently probes tenant-pm for a persona física and turns a ready report
  // into an undownloadable "Pendiente" with no telemetry at all.
  // So don't map anything: scan the subfolders that actually exist and mark ready if the
  // principal tenant folder has a validated bdc-authorization. Anchored on purpose —
  // the additional-participant folders (tenant-pf-cja / tenant-pm-cja) must never be able
  // to mark the principal's Express report as ready.
  const tenantBdcCompleted = Object.keys(
    reportData.required_documents ?? {},
  ).some(
    (key) =>
      /^tenant-p[fm]$/.test(key) &&
      isRequirementValid(reportData, key, "bdc-authorization"),
  );

  data.push({
    name: "Multireporte Express del Inquilino",
    type: "tenant_credit_history",
    status: tenantBdcCompleted ? "completed" : "pending_upload",
    documentPassword: tenantDocumentPassword,
  });

  if (reportData.guarantor_investigation) {
    // Guarantor MReport.
    // The bdc-authorization can live in different guarantor subfolders depending on
    // how the VIF was contracted: guarantor-investigation-pf/pm (up front) or
    // guarantor-pf/pm (added later via the upsell). Scan every guarantor subfolder and
    // mark ready if any of them has a validated bdc-authorization — this avoids
    // depending on legal_type/suffix (which can be encoded inconsistently).
    const guarantorBdcCompleted = Object.keys(
      reportData?.required_documents ?? {},
    ).some(
      (key) =>
        key.toLowerCase().includes("guarantor") &&
        isRequirementValid(reportData, key, "bdc-authorization"),
    );

    data.push({
      name: "Multireporte Express del Fiador",
      type: "guarantor_credit_history",
      status: guarantorBdcCompleted ? "completed" : "pending_upload",
      documentPassword: getPersonPassword(reportData.guarantor_investigation),
    });
  }

  return data;
};
